import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { UserDirectoryItem } from "@/lib/axios/api/user-directory.api";
import { useDashboard } from "@/contexts/DashboardContext";

interface PstnUsersTableProps {
    users: UserDirectoryItem[];
    totalCount: number;
    currentPage: number;
    pageSize: number;
    onPageChange: (page: number) => void;
    loading?: boolean;
}

function getAvatarSource(image?: string): string | undefined {
    if (!image) return undefined;
    const trimmed = image.trim();
    if (!trimmed) return undefined;
    if (trimmed.startsWith("data:")) return trimmed;
    return `data:image/jpeg;base64,${trimmed}`;
}

function getInitials(name?: string): string {
    if (!name) return "NA";
    return name.slice(0, 2).toUpperCase();
}

const AVATAR_COLORS = [
    "bg-teal-400",
    "bg-blue-400",
    "bg-violet-400",
    "bg-rose-400",
    "bg-amber-400",
    "bg-emerald-500",
];

function getAvatarColorClass(name: string): string {
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

const PstnUsersTable: React.FC<PstnUsersTableProps> = ({
    users,
    totalCount,
    currentPage,
    pageSize,
    onPageChange,
    loading = false,
}) => {
    const { theme } = useDashboard();
    const totalPages = totalCount === 0 ? 1 : Math.ceil(totalCount / pageSize);
    const startItem = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
    const endItem = Math.min(currentPage * pageSize, totalCount);

    return (
        <Card className="w-full overflow-hidden dark:bg-[#0C3451] dark:text-white dark:border-[#0C3451] rounded-lg">
            <CardHeader className="border-b dark:border-[#1C5280] pb-3 min-h-[68px] flex flex-col justify-center">
                <h3 className="text-base font-semibold text-slate-900 dark:text-white">
                    PSTN Users
                </h3>
                <p className="text-xs text-muted-foreground mt-1">
                    Total: <span className="inline-block min-w-[5ch] text-left tabular-nums">{totalCount} users</span>
                </p>
            </CardHeader>

            <CardContent className="p-0 flex flex-col overflow-hidden">
                <div className="overflow-y-auto h-[240px]">
                    <table className="w-full text-xs">
                        <thead className="bg-muted/40 dark:bg-[#0E2636] sticky top-0 z-10">
                            <tr className="border-b dark:border-[#21394D]">
                                <th className="text-left px-3 py-2 font-medium text-slate-500 dark:text-slate-300">
                                    User
                                </th>
                                <th className="text-left px-3 py-2 font-medium text-slate-500 dark:text-slate-300">
                                    UPN
                                </th>
                                <th className="text-left px-3 py-2 font-medium text-slate-500 dark:text-slate-300">
                                    Phone Number
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                Array.from({ length: pageSize }).map((_, i) => (
                                    <tr key={i} className="border-b border-border/60 dark:border-[#122638]">
                                        <td className="px-3 py-2"><div className="h-4 w-32 rounded bg-muted animate-pulse" /></td>
                                        <td className="px-3 py-2"><div className="h-4 w-48 rounded bg-muted animate-pulse" /></td>
                                        <td className="px-3 py-2"><div className="h-4 w-24 rounded bg-muted animate-pulse" /></td>
                                    </tr>
                                ))
                            ) : totalCount === 0 ? (
                                <tr>
                                    <td colSpan={3} className="py-10 text-center text-sm text-muted-foreground">
                                        No PSTN users found
                                    </td>
                                </tr>
                            ) : (
                                users.map((user) => {
                                    const avatarSource = getAvatarSource(user.image);
                                    return (
                                        <tr
                                            key={user.id}
                                            className="border-b border-border/60 bg-white/60 hover:bg-muted/60 dark:bg-[#071827] dark:border-[#122638] dark:hover:bg-[#0E2636]"
                                        >
                                            <td className="px-3 py-2">
                                                <div className="flex items-center gap-2">
                                                    {avatarSource ? (
                                                        <img
                                                            src={avatarSource}
                                                            alt={user.displayName}
                                                            className="w-6 h-6 rounded-full object-cover flex-shrink-0"
                                                        />
                                                    ) : (
                                                        <div className={`w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center text-[9px] font-medium text-white ${getAvatarColorClass(user.displayName || "")}`}>
                                                            {getInitials(user.displayName)}
                                                        </div>
                                                    )}
                                                    <span className="truncate text-slate-900 dark:text-white">
                                                        {user.displayName}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-3 py-2 text-slate-600 dark:text-slate-400 truncate">
                                                {user.mail}
                                            </td>
                                            <td className="px-3 py-2 text-slate-600 dark:text-slate-400 whitespace-nowrap">
                                                {user.phoneNumber || "N/A"}
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Footer */}
                <div className="border-t dark:border-[#122638] px-3 py-2 flex items-center justify-between text-xs text-muted-foreground">
                    <div>
                        Showing {startItem}-{endItem} of {totalCount}
                    </div>
                    <div className="flex gap-1">
                        <Button
                            variant="ghost"
                            size="sm"
                            disabled={currentPage === 1 || loading}
                            onClick={() => onPageChange(currentPage - 1)}
                            className="h-7 w-7 p-0"
                        >
                            <ChevronLeft className="h-3 w-3" />
                        </Button>
                        <div className="px-2 py-1 text-xs font-medium">
                            {currentPage} / {totalPages}
                        </div>
                        <Button
                            variant="ghost"
                            size="sm"
                            disabled={currentPage >= totalPages || loading}
                            onClick={() => onPageChange(currentPage + 1)}
                            className="h-7 w-7 p-0"
                        >
                            <ChevronRight className="h-3 w-3" />
                        </Button>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

export default PstnUsersTable;
